/**
 * VoiceControl -- governed voice actions for the Command Center (2026-07-23).
 *
 * Governance discipline, mirrored from the server (api/voice-governance.js):
 *   - The server classifies; the CLIENT executes -- and only read-only
 *     navigation or draft-only chat text, only after the operator presses
 *     Confirm on the card. No confirm, no action.
 *   - The app-global summon hotkey (Ctrl+Shift+V) can only START a capture.
 *     It is ignored in every other phase, so it can never confirm.
 *   - Push-to-talk: hold the mic button (pointer-captured) or hold Space in
 *     the chat view for >=250ms; release stops. A quick tap toggles a
 *     VAD-auto-stop capture instead.
 *   - Audio is sent to the on-box transcriber only; when that engine is not
 *     enabled the card explains how to enable it -- there is no silent
 *     fallback to third-party speech services.
 *
 * Copy discipline: capture UI speaks about "your words" (dictation), never
 * "your voice".
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Square, X, Check } from 'lucide-react';
import { useVoiceCapture } from '../../hooks/useVoiceCapture';
import { getApiKey } from '../../lib/api';
import type { ViewId } from './CommandCenter';

const PTT_HOLD_MS = 250;
export const VOICE_DRAFT_EVENT = 'agentomy:voice-draft';

interface Classification {
  intent: string;
  params: Record<string, string>;
  kind: 'navigate' | 'draft' | 'none';
  view: string | null;
  path: string;
  explanation: string;
}

type Phase = 'idle' | 'capturing' | 'working' | 'confirm' | 'notice';

interface VoiceControlProps {
  currentView: ViewId;
  onNavigate: (view: ViewId) => void;
}

function apiHeaders(extra?: Record<string, string>): Record<string, string> {
  const key = getApiKey();
  return { ...(extra || {}), ...(key ? { 'X-API-Key': key } : {}) };
}

export default function VoiceControl({ currentView, onNavigate }: VoiceControlProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [transcript, setTranscript] = useState('');
  const [classification, setClassification] = useState<Classification | null>(null);
  const [notice, setNotice] = useState('');
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const handleAudio = useCallback(async (audio: Blob) => {
    setPhase('working');
    try {
      const sttRes = await fetch('/api/voice/transcribe', {
        method: 'POST',
        headers: apiHeaders({ 'Content-Type': audio.type || 'application/octet-stream' }),
        body: audio,
      });
      if (sttRes.status === 503) {
        const body = await sttRes.json().catch(() => ({}));
        setNotice(body.howToEnable || 'On-box speech-to-text is not enabled on this deployment.');
        setPhase('notice');
        return;
      }
      if (!sttRes.ok) {
        setNotice('Transcription failed. Your words were not captured; nothing was executed.');
        setPhase('notice');
        return;
      }
      const { text } = await sttRes.json();
      if (!text || !text.trim()) {
        setNotice('No speech was recognized. Nothing was executed.');
        setPhase('notice');
        return;
      }
      setTranscript(text);
      const clsRes = await fetch('/api/voice/classify', {
        method: 'POST',
        headers: apiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ text }),
      });
      if (!clsRes.ok) {
        setNotice('Classification failed. Nothing was executed.');
        setPhase('notice');
        return;
      }
      const result: Classification = await clsRes.json();
      setClassification(result);
      setPhase('confirm');
    } catch {
      setNotice('Voice request failed. Nothing was executed.');
      setPhase('notice');
    }
  }, []);

  const capture = useVoiceCapture(handleAudio);

  const beginCapture = useCallback((mode: 'vad' | 'ptt') => {
    // A capture can only begin from idle -- the summon path can never
    // interact with an open confirm card.
    if (phaseRef.current !== 'idle') { return; }
    setTranscript('');
    setClassification(null);
    void capture.start(mode).then((started) => {
      if (started) { setPhase('capturing'); }
      else if (capture.state === 'unsupported') {
        setNotice('This browser does not support microphone capture.');
        setPhase('notice');
      } else {
        setNotice('Microphone permission was not granted. Nothing was captured.');
        setPhase('notice');
      }
    });
  }, [capture]);

  const dismiss = useCallback(() => {
    setClassification(null);
    setTranscript('');
    setNotice('');
    setPhase('idle');
  }, []);

  const confirm = useCallback(() => {
    const cls = classification;
    dismiss();
    if (!cls || cls.kind === 'none') { return; }
    // CLIENT-side execution of a whitelisted, confirmed intent only.
    if (cls.kind === 'navigate' && cls.view) {
      onNavigate(cls.view as ViewId);
    } else if (cls.kind === 'draft' && cls.params?.['text']) {
      window.dispatchEvent(new CustomEvent(VOICE_DRAFT_EVENT, { detail: { text: cls.params['text'] } }));
      onNavigate('chat');
    }
  }, [classification, dismiss, onNavigate]);

  // Pointer push-to-talk on the mic button: hold >=250ms (pointer-captured)
  // talks until release; a quick tap toggles a VAD-auto-stop capture.
  const pttTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pttActiveRef = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    if (phaseRef.current === 'capturing') { return; } // release/tap handled on pointer-up
    pttTimerRef.current = setTimeout(() => {
      pttActiveRef.current = true;
      beginCapture('ptt');
    }, PTT_HOLD_MS);
  }, [beginCapture]);

  const onPointerUp = useCallback(() => {
    if (pttTimerRef.current) { clearTimeout(pttTimerRef.current); pttTimerRef.current = null; }
    if (pttActiveRef.current) {
      pttActiveRef.current = false;
      capture.stop();
      return;
    }
    // Quick tap: toggle VAD capture.
    if (phaseRef.current === 'capturing') { capture.stop(); }
    else { beginCapture('vad'); }
  }, [beginCapture, capture]);

  // App-global keyboard wiring:
  //  - Ctrl+Shift+V summons a VAD capture -- START only, from idle only.
  //  - Space-hold (>=250ms) in the chat view is push-to-talk.
  const spaceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spacePttRef = useRef(false);

  useEffect(() => {
    const isEditable = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      const tag = el?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || !!el?.isContentEditable;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditable(e.target)) { return; }
      if (e.ctrlKey && e.shiftKey && (e.key === 'V' || e.key === 'v')) {
        e.preventDefault();
        beginCapture('vad'); // no-op unless idle: the summon can only start
        return;
      }
      if (e.key === ' ' && currentView === 'chat' && !e.repeat && phaseRef.current === 'idle' && !spaceTimerRef.current) {
        e.preventDefault();
        spaceTimerRef.current = setTimeout(() => {
          spacePttRef.current = true;
          beginCapture('ptt');
        }, PTT_HOLD_MS);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== ' ') { return; }
      if (spaceTimerRef.current) { clearTimeout(spaceTimerRef.current); spaceTimerRef.current = null; }
      if (spacePttRef.current) {
        spacePttRef.current = false;
        capture.stop();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [beginCapture, capture, currentView]);

  const capturing = phase === 'capturing';
  const stateLabel = capture.state === 'calibrating' ? 'Calibrating...' : 'Listening for your words...';

  return (
    <>
      <button
        type="button"
        data-testid="voice-mic-button"
        aria-label={capturing ? 'Stop voice capture' : 'Start voice capture (hold to talk, tap for auto-stop)'}
        title="Voice: tap for auto-stop capture, hold to talk. Ctrl+Shift+V summons."
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        className={`fixed bottom-6 right-24 z-40 flex h-12 w-12 items-center justify-center rounded-full border transition-colors ${
          capturing
            ? 'border-red-500 bg-red-500/20 text-red-400 animate-pulse'
            : 'border-[#3A352F] bg-[#1C1917] text-[#A8A29E] hover:text-[#F5F0E8] hover:border-[#5A534B]'
        }`}
      >
        {capturing ? <Square size={18} /> : <Mic size={18} />}
      </button>

      {capturing && (
        <div
          data-testid="voice-capture-state"
          className="fixed bottom-20 right-24 z-40 rounded-md border border-[#3A352F] bg-[#1C1917] px-3 py-1.5 text-xs text-[#A8A29E]"
        >
          {stateLabel}
        </div>
      )}

      {phase === 'notice' && (
        <div
          data-testid="voice-notice"
          className="fixed bottom-20 right-6 z-50 w-96 rounded-lg border border-[#3A352F] bg-[#1C1917] p-4 text-sm text-[#F5F0E8] shadow-xl"
        >
          <div className="mb-2 font-semibold">Voice</div>
          <p className="mb-3 text-[#A8A29E]">{notice}</p>
          <button
            type="button"
            data-testid="voice-dismiss-button"
            onClick={dismiss}
            className="rounded border border-[#3A352F] px-3 py-1 text-xs hover:border-[#5A534B]"
          >
            Dismiss
          </button>
        </div>
      )}

      {phase === 'confirm' && classification && (
        <div
          data-testid="voice-confirm-card"
          className="fixed bottom-20 right-6 z-50 w-96 rounded-lg border border-[#3A352F] bg-[#1C1917] p-4 text-sm text-[#F5F0E8] shadow-xl"
        >
          <div className="mb-1 font-semibold">
            {classification.kind === 'none' ? 'Not available by voice' : 'Confirm voice action'}
          </div>
          {transcript && (
            <p data-testid="voice-transcript" className="mb-2 border-l-2 border-[#3A352F] pl-2 italic text-[#A8A29E]">
              "{transcript}"
            </p>
          )}
          <p className="mb-3 text-[#A8A29E]">{classification.explanation}</p>
          {classification.kind === 'draft' && classification.params?.['text'] && (
            <p className="mb-3 rounded bg-black/30 p-2 text-xs text-[#F5F0E8]">
              Draft (placed in the chat input, not sent): {classification.params['text']}
            </p>
          )}
          <div className="flex gap-2">
            {classification.kind !== 'none' && (
              <button
                type="button"
                data-testid="voice-confirm-button"
                onClick={confirm}
                className="flex items-center gap-1 rounded border border-emerald-600 bg-emerald-600/15 px-3 py-1 text-xs text-emerald-400 hover:bg-emerald-600/25"
              >
                <Check size={12} /> Confirm
              </button>
            )}
            <button
              type="button"
              data-testid="voice-cancel-button"
              onClick={dismiss}
              className="flex items-center gap-1 rounded border border-[#3A352F] px-3 py-1 text-xs hover:border-[#5A534B]"
            >
              <X size={12} /> {classification.kind === 'none' ? 'Dismiss' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
